import React, { useEffect, useRef, useState } from "react";
import { buildGoogleCalendarUrl, buildIcs, buildOutlookCalendarUrl } from "../../lib/googleCalendarLink";
import { shareTerminViaWhatsApp } from "../../lib/shareTermin";
import { trackSchoolCalendarTap } from "../../lib/wedgeFunnel";
import { ANALYTICS } from "../../lib/analyticsEvents";
import { trackEvent } from "../../analytics";
import { patchScheduleItem } from "../../api/scheduleItems";
import { fetchFamilyProfile } from "../../api/familyProfile";
import { namedChildrenForCapture } from "../../lib/captureChat";
import { applyScheduleFieldPatch, isMailScheduleItem, originFromSavedItem } from "../../lib/heuteScheduleMerge";
import { isDefinitiveMutationError } from "../../lib/pendingMutations";
import { FORGET_COPY } from "../../lib/extractionForget";
import { isEventActionType } from "../../lib/schoolAttention";
import {
  CALENDAR_PROVIDER_LABEL,
  calendarProviderOrder,
  readCalendarProvider,
  rememberCalendarProvider,
} from "../../lib/calendarPreference";
import {
  VISIBILITY_HOUSEHOLD,
  VISIBILITY_LABEL_HOUSEHOLD,
  VISIBILITY_LABEL_MINE,
  VISIBILITY_MINE,
} from "../../lib/scheduleVisibility";
import "../../pages/SchoolInboxPage.css";
import EventLineFields from "../EventLineFields/EventLineFields";

function downloadIcs(event) {
  const ics = buildIcs(event);
  if (!ics) return;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(event.title || "termin").replace(/[^\w-]+/g, "_").slice(0, 40)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function childOptions(names, current) {
  const list = (names || []).filter(Boolean);
  if (current && !list.includes(current)) return [current, ...list];
  return list;
}

function ChildField({ name, options, disabled, onCommit }) {
  const [open, setOpen] = useState(false);
  if (!name && options.length === 0) return null;
  if (!open) {
    return (
      <button
        type="button"
        className="heute-sheet-child"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {name || "Kind"}
      </button>
    );
  }
  return (
    <select
      className="heute-sheet-child-select"
      aria-label="Kind"
      autoFocus
      defaultValue={name || ""}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value || null;
        setOpen(false);
        if ((next || "") !== (name || "")) onCommit(next);
      }}
      onBlur={() => setOpen(false)}
    >
      <option value="">—</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

export default function HeuteScheduleSheet({
  item,
  open,
  householdLinked,
  onClose,
  onRefresh,
  onFieldsChange,
  onToggleRemind,
  onIrrelevant,
  analyticsSource = "heute",
}) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(item);
  const [visibility, setVisibility] = useState(item?.visibility);
  const [remindEnabled, setRemindEnabled] = useState(!!item?.remindEnabled);
  const [childNames, setChildNames] = useState([]);
  const [calendarProvider, setCalendarProvider] = useState(readCalendarProvider);
  const mountedRef = useRef(true);
  const openedRef = useRef({ open: false, id: null });

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  useEffect(() => {
    const id = item?.id || null;
    if (!open) {
      setError(null);
      openedRef.current = { open: false, id };
      return;
    }
    if (openedRef.current.open && openedRef.current.id === id) return;
    openedRef.current = { open: true, id };
    setDraft(item);
    setVisibility(item?.visibility);
    setRemindEnabled(!!item?.remindEnabled);
    setCalendarProvider(readCalendarProvider());
  }, [open, item]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchFamilyProfile()
      .then((profile) => {
        if (cancelled) return;
        setChildNames(namedChildrenForCapture(profile?.children).map((child) => child.name));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open || !item) return null;

  const view = draft && draft.id === item.id ? draft : item;
  const isMail = isMailScheduleItem(view);
  const resolved = {
    ...view,
    ...(view.rawEvent || {}),
  };
  const calUrl = buildGoogleCalendarUrl(resolved);
  const outlookUrl = buildOutlookCalendarUrl(resolved);
  const canAddToCalendar = Boolean(calUrl || outlookUrl);
  const canShare = item.isEvent || isEventActionType(resolved.action_type);
  const canSetVisibility = householdLinked && item.id;
  const canToggleRemind = Boolean(item.id && onToggleRemind);
  const canEdit = Boolean(item.id);
  const origin = originFromSavedItem(view);
  const mailSubject = origin === "mail" ? String(view.mailSubject || "").replace(/\s+/g, " ").trim() : "";
  const canIrrelevant = Boolean(
    onIrrelevant &&
      ((origin === "mail" && item.extractionId != null && Number.isInteger(item.eventIndex)) ||
        (origin === "capture" && item.id)),
  );

  const handleRemind = () => {
    if (!canToggleRemind || busy) return;
    const current = remindEnabled;
    setRemindEnabled(!current);
    onToggleRemind({ ...item, remindEnabled: current });
  };

  const paintFields = (patch) => {
    if (Object.prototype.hasOwnProperty.call(patch, "visibility")) setVisibility(patch.visibility);
    setDraft((current) => applyScheduleFieldPatch(current && current.id === item.id ? current : item, patch));
    onFieldsChange?.(item, patch);
  };

  const commitFields = (patch) => {
    if (!item.id) return;
    const source = view;
    const previous = {};
    for (const key of Object.keys(patch || {})) previous[key] = source?.[key];
    paintFields(patch);
    setError(null);
    void (async () => {
      try {
        await patchScheduleItem(item.id, patch);
        if (mountedRef.current && onRefresh) onRefresh();
      } catch (err) {
        if (!isDefinitiveMutationError(err) || !mountedRef.current) return;
        paintFields(previous);
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  };

  const handleVisibility = () => {
    if (!canSetVisibility || busy) return;
    const next = visibility === VISIBILITY_HOUSEHOLD ? VISIBILITY_MINE : VISIBILITY_HOUSEHOLD;
    commitFields({ visibility: next });
  };

  const handleShare = async () => {
    setBusy("share");
    setError(null);
    try {
      await shareTerminViaWhatsApp({
        ...resolved,
        source: analyticsSource,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };


  const chooseCalendar = (provider) => {
    rememberCalendarProvider(provider);
    setCalendarProvider(provider);
    if (isMail && item.extractionId) {
      trackSchoolCalendarTap({
        extraction_id: item.extractionId,
        event_index: item.eventIndex,
        source: analyticsSource,
        provider,
      });
    }
  };

  return (
    <div className="school-edit-backdrop" role="presentation" onClick={onClose}>
      <div
        className="school-edit-sheet heute-schedule-sheet"
        role="dialog"
        aria-label={view.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="heute-sheet-fact">
          <EventLineFields
            title={view.title}
            date={view.date}
            endDate={view.end_date || resolved.end_date || ""}
            time={view.time || ""}
            allDay={view.all_day !== false && !view.time}
            editable={canEdit}
            disabled={busy === "share"}
            onCommit={commitFields}
          />
          <ChildField
            name={view.child || ""}
            options={childOptions(childNames, view.child || "")}
            disabled={busy === "share"}
            onCommit={(child) => commitFields({ child })}
          />
          {mailSubject ? <p className="heute-sheet-subject">Betreff: {mailSubject}</p> : null}
          {view.overdue ? <p className="heute-sheet-meta">überfällig</p> : null}
          {resolved.source_quote || item.rawEvent?.source_quote ? (
            <p className="school-event-quote">
              „{resolved.source_quote || item.rawEvent.source_quote}"
            </p>
          ) : null}
          {error ? <p className="school-error">{error}</p> : null}
        </div>
        {canSetVisibility || canToggleRemind ? (
          <div className="heute-sheet-status">
            {canSetVisibility ? (
              <button
                type="button"
                className={
                  visibility === VISIBILITY_HOUSEHOLD
                    ? "heute-sheet-chip heute-sheet-chip--on"
                    : "heute-sheet-chip"
                }
                disabled={busy !== null}
                aria-pressed={visibility === VISIBILITY_HOUSEHOLD}
                aria-label={
                  visibility === VISIBILITY_HOUSEHOLD
                    ? "Für uns, tippen für Nur ich"
                    : "Nur ich, tippen für Für uns"
                }
                onClick={handleVisibility}
              >
                {visibility === VISIBILITY_HOUSEHOLD ? VISIBILITY_LABEL_HOUSEHOLD : VISIBILITY_LABEL_MINE}
              </button>
            ) : null}
            {canToggleRemind ? (
              <button
                type="button"
                className={remindEnabled ? "heute-sheet-chip heute-sheet-chip--on" : "heute-sheet-chip"}
                disabled={busy !== null}
                aria-pressed={remindEnabled}
                onClick={handleRemind}
              >
                {remindEnabled ? "Erinnerung an" : "Erinnerung aus"}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="heute-sheet-actions">
            {canAddToCalendar ? (
              <div className="heute-sheet-calendar">
                {calendarProviderOrder(calendarProvider).map((provider) => {
                  const href = provider === "google" ? calUrl : outlookUrl;
                  if (!href) return null;
                  const quiet = calendarProvider && calendarProvider !== provider;
                  return (
                    <a
                      key={provider}
                      className={`heute-sheet-pill${quiet ? " heute-sheet-pill--quiet" : ""}`}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => chooseCalendar(provider)}
                    >
                      {CALENDAR_PROVIDER_LABEL[provider]}
                    </a>
                  );
                })}
              </div>
            ) : null}
            {canShare || canAddToCalendar || canIrrelevant ? (
              <div className="heute-sheet-quiet">
                {canShare ? (
                  <button
                    type="button"
                    className="heute-sheet-quiet-btn"
                    disabled={busy !== null}
                    onClick={handleShare}
                  >
                    {busy === "share" ? "…" : "Teilen"}
                  </button>
                ) : null}
                {canIrrelevant ? (
                  <button
                    type="button"
                    className="heute-sheet-quiet-btn"
                    disabled={busy !== null}
                    onClick={() => {
                      onIrrelevant(item);
                      onClose();
                    }}
                  >
                    {FORGET_COPY.irrelevant}
                  </button>
                ) : null}
                {canAddToCalendar ? (
                  <button
                    type="button"
                    className="heute-sheet-quiet-btn"
                    disabled={busy !== null}
                    onClick={() => {
                      if (isMail && item.extractionId) {
                        trackEvent(ANALYTICS.school_ics_download, {
                          extraction_id: item.extractionId,
                          event_index: item.eventIndex,
                          source: analyticsSource,
                        });
                      }
                      downloadIcs(resolved);
                    }}
                  >
                    .ics
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
      </div>
    </div>
  );
}
